import classNames from "classnames";
import { FC, useCallback } from "react";

import { ReactComponent as CopyIcon } from "assets/images/common/copy.svg";

import Button from "components/Button";
import Modal from "components/Modal";

import { useAppDispatch } from "store/hooks";
import { hideModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";

import { LORAWAN_SERVER_HOST, LORAWAN_SERVER_PORT } from "utils/constants";

import styles from "./styles.module.scss";

type PropTypes = {
  isVisible: boolean;
  className?: string;
  gatewayName?: string;
  gatewayEui?: string;
};

const WidgetModalGatewayConfigInstructions: FC<PropTypes> = ({
  isVisible,
  className,
  gatewayName,
  gatewayEui,
}) => {
  const dispatch = useAppDispatch();

  const serverUri = `${LORAWAN_SERVER_HOST}:${LORAWAN_SERVER_PORT}`;

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const handleCopy = useCallback(
    (label: string, value: string) => async () => {
      try {
        await navigator.clipboard.writeText(value);
        dispatch(
          showToast({
            id: `gateway-${label}-copied-${Date.now()}`,
            message: `${label} copied to clipboard.`,
            type: "success",
          }),
        );
      } catch {
        /* clipboard access denied — silently ignore */
      }
    },
    [dispatch],
  );

  return (
    <Modal
      title="Configuration instructions"
      isClose
      isCloseBg
      isVisible={isVisible}
      className={classNames(styles.modal, className)}
    >
      <p className={styles.description}>
        {gatewayName ? `Gateway "${gatewayName}" was registered.` : "Your gateway was registered."}{" "}
        Configure the packet forwarder on the device to forward LoRaWAN frames to the network
        server below.
      </p>

      <div className={styles.body}>
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Network server (Semtech UDP)</span>
          <div className={styles.tokenBox}>
            {serverUri}
            <button
              type="button"
              className={styles.copyButton}
              onClick={handleCopy("Server address", serverUri)}
              aria-label="Copy server address"
            >
              <CopyIcon />
            </button>
          </div>
          <p className={styles.sectionText}>
            Enter the host part as <strong>Server Address</strong> and{" "}
            <strong>{LORAWAN_SERVER_PORT}</strong> as <strong>Port</strong> in your gateway's
            packet-forwarder settings.
          </p>
        </div>

        {gatewayEui && (
          <div className={styles.section}>
            <span className={styles.sectionTitle}>Gateway EUI</span>
            <div className={styles.tokenBox}>
              {gatewayEui}
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopy("Gateway EUI", gatewayEui)}
                aria-label="Copy gateway EUI"
              >
                <CopyIcon />
              </button>
            </div>
          </div>
        )}

        <div className={styles.section}>
          <span className={styles.sectionTitle}>Next steps</span>
          <ol className={styles.steps}>
            <li>Power on the gateway and connect it to your local network.</li>
            <li>Open the gateway admin UI and navigate to "Packet Forwarder" → "General".</li>
            <li>
              Set <strong>Mode</strong> to <strong>Semtech UDP</strong>,{" "}
              <strong>Server Address</strong> to <code>{LORAWAN_SERVER_HOST}</code>, and{" "}
              <strong>Port</strong> (uplink and downlink) to{" "}
              <strong>{LORAWAN_SERVER_PORT}</strong>.
            </li>
            <li>
              Set <strong>Frequency Band</strong> to <strong>EU868</strong>. Save the
              configuration and restart the packet forwarder.
            </li>
          </ol>
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="button" variant="primary" onClick={handleClose}>
          Got it
        </Button>
      </div>
    </Modal>
  );
};

export default WidgetModalGatewayConfigInstructions;
